import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Store, Building2, ArrowRight } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import PartnerShopSection from "@/components/public/PartnerShopSection";
import SafeImage from "@/components/SafeImage";

/**
 * Phase 14.2 — Simplified Shop with full-bleed hero cards from uploaded
 * artwork, plus a Partner Storefronts section underneath.
 */

const GEAR_IMAGE =
  "https://customer-assets.emergentagent.com/job_lifted-core/artifacts/40hsd1os_F102DB47-B3FD-4CF3-88B6-73FB7872E6DB.png";
const ACCESSORIES_IMAGE =
  "https://customer-assets.emergentagent.com/job_lifted-core/artifacts/0lq8if7a_20240585-1485-4083-819D-F95E554AC9A9.png";

export default function Shop() {
  const [partners, setPartners] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shop/collections/partner-products");
        setPartners(data?.products || []);
      } catch (e) {
        setErr(apiErrorMessage(e));
        setPartners([]);
      }
    })();
  }, []);

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <PageMeta title="Shop" path="/shop" ogImage="/og-shop.png"
                description="Shop the official Lifted catalog — Lifted Gear (clothing) and Lifted Accessories. Affiliate-eligible. Ships nationally; same-day in select markets." />
      <header className="mb-10">
        <div className="eyebrow">Shop Lifted</div>
        <h1 className="font-anton text-4xl sm:text-5xl uppercase mt-2">
          The Lifted catalog
        </h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          Two flagship lines — Lifted Gear (clothing) and Lifted Accessories
          (smoke &amp; lifestyle). Stickers live as a subcategory inside both.
        </p>
      </header>

      {/* Two flagship hero cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Link
          to="/collections/lifted-gear"
          data-testid="shop-tile-lifted-gear"
          className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10 hover:border-lifted-green/60 transition-colors bg-black"
        >
          <SafeImage
            src={GEAR_IMAGE}
            alt="Lifted Gear"
            className="absolute inset-0 w-full h-full object-contain object-center transition-transform duration-700 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-lifted-green">
              Lifted line · clothing
            </div>
            <div className="mt-1 inline-flex items-center gap-2 text-white font-anton text-2xl uppercase">
              Shop Lifted Gear <ArrowRight size={16} />
            </div>
          </div>
        </Link>

        <Link
          to="/collections/lifted-accessories"
          data-testid="shop-tile-lifted-accessories"
          className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10 hover:border-lifted-green/60 transition-colors bg-black"
        >
          <SafeImage
            src={ACCESSORIES_IMAGE}
            alt="Lifted Accessories"
            className="absolute inset-0 w-full h-full object-contain object-center transition-transform duration-700 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-lifted-green">
              Lifted line · accessories
            </div>
            <div className="mt-1 inline-flex items-center gap-2 text-white font-anton text-2xl uppercase">
              Shop Lifted Accessories <ArrowRight size={16} />
            </div>
          </div>
        </Link>
      </div>

      {/* Section 2 — Partner shop directory (clickable logos → per-partner storefront) */}
      <PartnerShopSection />

      {/* Partner Storefronts section */}
      <section className="mt-16" data-testid="partner-storefronts-section">
        <div className="border-t border-white/10 pt-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="eyebrow">Partner storefronts</div>
              <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2">
                Join the Lifted partner network
              </h2>
              <p className="text-lifted-dim mt-3 max-w-2xl">
                Would you like to have your storefront on Lifted At The Moment?
                Apply as a partner store or explore a franchise opportunity to
                appear here when your inventory goes live.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Link
                to="/apply/partner-store"
                data-testid="shop-apply-partner"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg bg-lifted-green text-black font-bebas hover:bg-lifted-green/90"
              >
                <Store size={14} /> Apply as Partner Store
              </Link>
              <Link
                to="/apply/partner-brand"
                data-testid="shop-apply-partner-brand"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg border border-white/20 text-white font-bebas hover:bg-white/5"
              >
                <Plus size={14} /> Apply as Brand
              </Link>
              <Link
                to="/apply/franchise"
                data-testid="shop-apply-franchise"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg border border-white/20 text-white font-bebas hover:bg-white/5"
              >
                <Building2 size={14} /> Explore Franchise
              </Link>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {partners.length === 0 ? (
              <Link
                to="/apply/partner"
                data-testid="shop-partner-placeholder"
                className="aspect-[3/4] border-2 border-dashed border-white/10 hover:border-lifted-green/40 rounded-2xl flex flex-col items-center justify-center text-center p-6 transition-colors group"
              >
                <div className="h-14 w-14 rounded-full border border-white/15 group-hover:border-lifted-green/60 flex items-center justify-center mb-4 transition-colors">
                  <Plus size={24} className="text-white/40 group-hover:text-lifted-green" />
                </div>
                <div className="font-anton text-lg uppercase text-white">
                  Add Your Store Here
                </div>
                <p className="text-xs text-lifted-dim mt-2 max-w-xs">
                  Approved partner stores and franchise locations will appear
                  here as their inventory goes live.
                </p>
                <span className="text-[10px] uppercase tracking-wider text-lifted-green mt-4 inline-flex items-center gap-1">
                  Apply as Partner Store <ArrowRight size={10} />
                </span>
              </Link>
            ) : (
              partners.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/${p.slug}`}
                  data-testid={`partner-product-${p.slug}`}
                  className="group block"
                >
                  <div className="aspect-[3/4] overflow-hidden rounded-xl bg-lifted-surface border border-white/5 relative">
                    {p.media?.[0] && (
                      <SafeImage
                        src={p.media[0]}
                        alt={p.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                  </div>
                  <div className="mt-3 text-sm text-lifted-text group-hover:text-lifted-green">
                    {p.title}
                  </div>
                  <div className="text-xs text-lifted-mute mt-0.5">
                    {(() => {
                      const c = p.price_cents ?? Math.round((p.price || 0) * 100);
                      return c ? `$${(c / 100).toFixed(2)} USD` : "Price not available";
                    })()}
                  </div>
                </Link>
              ))
            )}
          </div>

          {err && (
            <p className="text-[11px] text-rose-300 mt-3" data-testid="shop-partners-error">
              {err}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
